/*****************************************************************
 *  CUSTOMER BUTLER - ONE PROPERTY (clean look, Zoho's layout)     *
 *  File: apps/web/src/pages/customer/PropertyRecordPage.js        *
 *  Address: /app/properties/<id>                                  *
 *  What it does: the property's name, then Zoho's Property        *
 *  related lists. "Details" shows Zoho's Property sections        *
 *  (Property Information, Location, New Section 2, New Section 1) *
 *  with every field in Zoho's order; any field can be changed in  *
 *  place.                                                         *
 *  Reads: the layout door, the property door, and (Reports tab)   *
 *  the Report Engine's lookup door.                               *
 *  Writes: the property, through its own door (PUT).              *
 *****************************************************************/

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from './customerIcons';
import { PageFrame, QuietLine, Notice } from './customerParts';
import { RecordSections, useSaveOneField } from './RecordSections';
import { TabRow, ReportsTab, NotConnectedTab } from './RecordTabs';
import { ActivitiesTab, PeopleTab, streetWords } from './RelatedLists';
import { fetchLayout, fetchProperty, saveProperty } from './customerApi';

/*****************************************************************
 *  SECTION 1 - LOADING THE PROPERTY AND THE LAYOUT                *
 *****************************************************************/

/* ---- usePropertyRecord(propertyId) -----------------------------
   Out: { property, layout, problemWords, setProperty }.         */
export function usePropertyRecord(propertyId) {
  const [property, setProperty] = useState(null);
  const [layout, setLayout] = useState(null);
  const [problemWords, setProblemWords] = useState('');

  useEffect(() => {
    Promise.all([fetchProperty(propertyId), fetchLayout('Property')])
      .then(([loadedProperty, loadedLayout]) => {
        setProperty(loadedProperty);
        setLayout(loadedLayout);
      })
      .catch(() => setProblemWords('This property could not be found, or the connection is down.'));
  }, [propertyId]);

  return { property, layout, problemWords, setProperty };
}

/*****************************************************************
 *  SECTION 2 - THE TAB BODIES                                     *
 *****************************************************************/

/* ---- TabBody({ tabKey, tabs, property, layout, onSaveField }) ---- */
function TabBody({ tabKey, tabs, property, layout, onSaveField }) {
  const chosenTab = tabs.find((oneTab) => oneTab.key === tabKey) || tabs[0];

  if (!chosenTab.isConnected) {
    return <NotConnectedTab label={chosenTab.label} />;
  }

  if (tabKey === 'details') {
    return <RecordSections record={property} sections={layout.sections} onSaveField={onSaveField} />;
  }

  if (tabKey === 'contacts') {
    return <PeopleTab owner={property.owner} linkedPeople={property.linkedPeople} />;
  }

  if (tabKey === 'reports') {
    const lookupAddress = property.addressLine1 || property.label || streetWords(property);

    return <ReportsTab email="" addresses={[lookupAddress]} />;
  }

  if (tabKey === 'activities') {
    return <ActivitiesTab propertyId={property.id} />;
  }

  return <NotConnectedTab label={chosenTab.label} />;
}

/*****************************************************************
 *  SECTION 3 - THE PAGE                                           *
 *****************************************************************/

/* ---- PropertyRecordPage({ onChooseSkin }) ---------------------- */
export function PropertyRecordPage({ onChooseSkin }) {
  const { id: propertyId } = useParams();
  const { property, layout, problemWords, setProperty } = usePropertyRecord(propertyId);
  const [tabKey, setTabKey] = useState('details');

  // WHY (2026-10-15): one copy of the save rule, shared with the HR look.
  const saveOneField = useSaveOneField(saveProperty, propertyId, setProperty);

  if (problemWords) {
    return (
      <PageFrame onChooseSkin={onChooseSkin}>
        <Link className="cb-back" to="/properties"><Icon name="back" /> Properties</Link>
        <Notice tone="bad" icon="warn">{problemWords}</Notice>
      </PageFrame>
    );
  }

  if (!property || !layout) {
    return (
      <PageFrame onChooseSkin={onChooseSkin}>
        <QuietLine icon="clock">Loading the property</QuietLine>
      </PageFrame>
    );
  }

  const nameWords = property.label || streetWords(property) || 'Unnamed property';
  const ownerName = property.owner ? property.owner.displayName : '';
  const peopleCount = (property.owner ? 1 : 0) + (property.linkedPeople || []).filter((onePerson) => !property.owner || onePerson.id !== property.owner.id).length;

  return (
    <PageFrame onChooseSkin={onChooseSkin}>
      <Link className="cb-back" to="/properties"><Icon name="back" /> Properties</Link>
      <header className="cb-head">
        <div>
          <h1 className="cb-title">{nameWords}</h1>
          {ownerName ? (
            <p className="cb-sub">Owner: <Link to={`/customers/${property.owner.id}`}>{ownerName}</Link></p>
          ) : null}
        </div>
      </header>

      <TabRow tabs={layout.relatedTabs} chosenKey={tabKey} onChoose={setTabKey} countFor={(oneKey) => (oneKey === 'contacts' ? peopleCount : undefined)} />

      <TabBody tabKey={tabKey} tabs={layout.relatedTabs} property={property} layout={layout} onSaveField={saveOneField} />
    </PageFrame>
  );
}

export default PropertyRecordPage;
